import {
  DevHubBox,
  DevHubButton,
  DevHubChip,
  DevHubDynamicIcon,
  DevHubGrid,
  DevHubMenuItem,
  DevHubSelect,
  DevHubTooltip,
  DevHubTypography,
} from '@devxdiscover/devhub-ui';
import { TargetUserDisplay } from '../types';
import { FILETYPE } from '../utils/constants';
import { useCGAHierarchyStyles } from '../styles';

interface ReportHeaderProps {
  targetUserDisplay: TargetUserDisplay;
  hierarchyLevelLabel: string;
  exportFormat: FILETYPE;
  onExportFormatChange: (event: React.ChangeEvent<{ value: unknown }>) => void;
  onExport: () => void;
  onCancel: () => void;
  isExporting: boolean;
  selectedCount: number;
  totalCount: number;
  loading: boolean;
}

/**
 * Report header component displaying user info and export controls.
 * Shows target user name, login, title, and allows selecting export format and initiating export.
 * Also shows export progress and allows canceling export when in progress.
 * @param props - Component props
 * @param props.targetUserDisplay - Display information for the target user
 * @param props.hierarchyLevelLabel - Label for the selected hierarchy level
 * @param props.exportFormat - Currently selected export format
 * @param props.onExportFormatChange - Callback when export format changes
 * @param props.onExport - Callback to initiate export
 * @param props.onCancel - Callback to cancel ongoing export
 * @param props.isExporting - Whether an export is currently in progress
 * @param props.selectedCount - Number of selected applications for export
 * @param props.totalCount - Total number of applications available for export
 * @param props.loading - Whether data is currently loading (disables controls)
 * @returns ReportHeader component
 */
export const ReportHeader = ({
  targetUserDisplay,
  hierarchyLevelLabel,
  exportFormat,
  onExportFormatChange,
  onExport,
  onCancel,
  isExporting,
  selectedCount,
  totalCount,
  loading,
}: ReportHeaderProps) => {
  const classes = useCGAHierarchyStyles();

  const exportButtonLabel =
    selectedCount > 0
      ? `Export Selected (${selectedCount})`
      : `Export All (${totalCount})`;

  const exportTooltip =
    selectedCount > 0
      ? `Export full report for ${selectedCount} selected application(s)`
      : `Export full report for all ${totalCount} application(s)`;

  return (
    <DevHubBox className={classes.reportHeader}>
      <DevHubGrid container spacing={1} alignItems="center">
        <DevHubGrid item xs>
          <DevHubTypography className={classes.primaryExportSubtitle}>
            Export full resource inventory report for{' '}
          </DevHubTypography>
          <DevHubTypography variant="h5">
            {targetUserDisplay.name}
            {targetUserDisplay.login
              ? ` (${targetUserDisplay.login.toLowerCase()})`
              : ''}
          </DevHubTypography>
          {targetUserDisplay.title ? (
            <DevHubTypography variant="body1" color="textSecondary">
              {targetUserDisplay.title}
            </DevHubTypography>
          ) : null}
        </DevHubGrid>
        <DevHubGrid item direction="column">
          <DevHubBox className={classes.exportActionsGroup}>
            <DevHubChip
              label={hierarchyLevelLabel}
              chipColor="blue"
              chipSize="small"
            />
            <DevHubSelect
              value={exportFormat}
              onChange={onExportFormatChange}
              disabled={totalCount === 0 || loading || isExporting}
              style={{ minWidth: '110px' }}
            >
              <DevHubMenuItem value={FILETYPE.EXCEL}>Excel</DevHubMenuItem>
              <DevHubMenuItem value={FILETYPE.CSV}>CSV</DevHubMenuItem>
            </DevHubSelect>

            <DevHubTooltip title={exportTooltip}>
              {isExporting ? (
                <DevHubButton
                  variant="outlined"
                  color="secondary"
                  onClick={onCancel}
                  startIcon={<DevHubDynamicIcon iconName="Clear" />}
                  size="large"
                >
                  Cancel Export
                </DevHubButton>
              ) : (
                <DevHubButton
                  variant="contained"
                  color="primary"
                  onClick={onExport}
                  disabled={totalCount === 0 || loading || isExporting}
                  startIcon={<DevHubDynamicIcon iconName="Save" />}
                  size="large"
                >
                  {exportButtonLabel}
                </DevHubButton>
              )}
            </DevHubTooltip>
          </DevHubBox>
          <DevHubTypography
            variant="caption"
            color="textSecondary"
            style={{ marginTop: '4px', marginLeft: '8rem' }}
          >
            Includes devices, cloud accounts, and CNAP tenancy data.
          </DevHubTypography>
        </DevHubGrid>
      </DevHubGrid>
    </DevHubBox>
  );
};
